import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import '../../css/growthWheel.scss'
import {
  useGetGrowthWheelByRoleIdQuery,
  useGetGrowthWheelWithEvaluationsByUserIdQuery,
} from '../../redux/growth-wheel.endpoints'
import {
  setGrowthWheelData,
  setUserCompetenciesData,
  setLoadingGrowthWheel,
  setCurrentCompetency,
  setCurrentSubCompetency,
  evaluateFocusCompetenciesLimit,
  evaluateGrowthWheelCompletion,
  setAssessmentCompletion,
  setCurrentAreasOfFocus,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { User } from '../../types/User'
import GrowthWheelDashboard from '../GrowthWheelDashboard/GrowthWheelDashboard'
import GrowthWheelCompetencyView from '../GrowthWheelCompetencyView/GrowthWheelCompetencyView'
import GrowthWheelSubCompetencyAssessment from '../GrowthWheelSubCompetencyView/GrowthWheelSubCompetencyView'

const GrowthWheel = ({ user }: { user: User }) => {
  const dispatch = useDispatch()
  const roleId = user.guildId

  const { data: growthWheelData, isLoading } = useGetGrowthWheelByRoleIdQuery(
    { roleId },
    { refetchOnMountOrArgChange: true },
  )
  const { data: userAssessmentData } =
    useGetGrowthWheelWithEvaluationsByUserIdQuery(
      { userId: user.id },
      { refetchOnMountOrArgChange: true },
    )
  const userCompetenciesData = userAssessmentData?.competencies

  const currentCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentCompetency,
  )
  const currentSubCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentSubCompetency,
  )

  useEffect(() => {
    dispatch(setGrowthWheelData(growthWheelData ?? null))
  }, [dispatch, growthWheelData])

  useEffect(() => {
    dispatch(setUserCompetenciesData(userCompetenciesData ?? []))
    dispatch(evaluateFocusCompetenciesLimit())
    dispatch(evaluateGrowthWheelCompletion())
    dispatch(setCurrentAreasOfFocus())
    dispatch(
      setAssessmentCompletion({
        isCompleted: userAssessmentData?.isCompleted ?? false,
        completedAt: userAssessmentData?.completedAt ?? null,
      }),
    )
  }, [dispatch, userCompetenciesData, userAssessmentData])

  useEffect(() => {
    dispatch(setLoadingGrowthWheel(isLoading))
  }, [dispatch, isLoading])

  useEffect(() => {
    if (!userCompetenciesData?.length) return
    if (currentCompetency) {
      const freshCompetency = userCompetenciesData.find(
        (c) => c.id === currentCompetency.id,
      )
      if (freshCompetency) {
        dispatch(setCurrentCompetency(freshCompetency))
        if (currentSubCompetency) {
          const freshSub = freshCompetency.subCompetencies?.find(
            (s) => s.id === currentSubCompetency.id,
          )
          if (freshSub) dispatch(setCurrentSubCompetency(freshSub))
        }
      }
    }
  }, [userCompetenciesData, currentCompetency, currentSubCompetency, dispatch])

  const displayAssessmentView = currentSubCompetency && currentCompetency
  const displayCompetencyView = currentCompetency && !currentSubCompetency
  const displayDashboardView = !currentCompetency && growthWheelData

  if (displayAssessmentView) {
    return <GrowthWheelSubCompetencyAssessment subjectUserId={user.id} />
  }
  if (displayCompetencyView) {
    return <GrowthWheelCompetencyView subjectUserId={user.id}/>
  }
  if (displayDashboardView) {
    return <GrowthWheelDashboard />
  }
  return null
}

export default GrowthWheel
